/**
 * Geofence Map Editor Component (Web)
 *
 * Allows users to:
 * - Search for locations by name using Geocoding API
 * - Draw circles and polygons on the map
 * - Edit existing geofences
 * - Save geometry as GeoJSON/WKT
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG, GEOFENCE_DRAWING_OPTIONS } from '../../config/maps';
import { circleToPolygonWkt, type LatLngLiteral } from './geofenceGeometry';

interface GeofenceMapEditorProps {
  initialGeometry?: any; // GeoJSON or WKT
  geometryType?: 'polygon' | 'circle';
  onGeometryChange: (geometry: { type: 'Polygon' | 'Circle'; coordinates?: any; center?: any; radius?: number; wkt: string }) => void;
  onCancel?: () => void;
}

const DEFAULT_ADDRESS_RADIUS_METERS = 200;
type Shape = google.maps.Circle | google.maps.Polygon;
type DrawingMode = 'circle' | 'polygon';

const toLatLngLiteral = (
  latLng: google.maps.LatLng | google.maps.LatLngLiteral | null
): LatLngLiteral | null => {
  if (!latLng) return null;

  return {
    lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
    lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
  };
};

const distanceInMeters = (from: LatLngLiteral, to: LatLngLiteral): number => {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const earthRadius = 6371000;
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const latitudeFactor = Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat));
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    latitudeFactor * Math.sin(deltaLng / 2) ** 2;

  return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const removeConsecutiveDuplicatePoints = (points: LatLngLiteral[]): LatLngLiteral[] =>
  points.filter((point, index) => {
    if (index === 0) return true;
    const previous = points[index - 1];
    return point.lat !== previous.lat || point.lng !== previous.lng;
  });

/**
 * Normalize geometry from any format (GeoJSON object, WKT string, etc.)
 * into a standard GeoJSON-like { type, coordinates } object.
 */
const normalizeGeometry = (
  geometry: any
): { type: string; coordinates: number[][][] } | null => {
  if (!geometry) return null;

  // Already a GeoJSON object with coordinates
  if (typeof geometry === 'object' && geometry.coordinates?.length) {
    return geometry;
  }

  // WKT string: parse POLYGON ((lng lat, lng lat, ...))
  if (typeof geometry === 'string') {
    const wkt = geometry.replace(/^SRID=\d+;/i, ''); // strip SRID prefix
    const match = wkt.match(/POLYGON\s*\(\((.+)\)\)/i);
    if (!match) return null;

    const coords = match[1].split(',').map((pair) => {
      const [lng, lat] = pair.trim().split(/\s+/).map(Number);
      return [lng, lat];
    });

    if (coords.length < 3 || coords.some(([a, b]) => isNaN(a) || isNaN(b))) return null;

    return { type: 'Polygon', coordinates: [coords] };
  }

  return null;
};

/**
 * Compute the centroid and a suitable zoom level from geometry
 * so the map can open already centred on the geofence.
 */
const computeCenterFromGeometry = (
  geometry: any
): { center: { lat: number; lng: number }; zoom: number } | null => {
  const normalized = normalizeGeometry(geometry);
  if (!normalized?.coordinates?.[0]?.length) return null;

  const ring = normalized.coordinates[0];
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;

  for (const [lng, lat] of ring) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  const center = {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  };

  // Estimate zoom from bounding-box span (rough heuristic)
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const span = Math.max(latSpan, lngSpan);
  let zoom = 14;
  if (span > 10) zoom = 5;
  else if (span > 5) zoom = 7;
  else if (span > 2) zoom = 8;
  else if (span > 1) zoom = 9;
  else if (span > 0.5) zoom = 10;
  else if (span > 0.1) zoom = 12;
  else if (span > 0.01) zoom = 14;
  else zoom = 16;

  return { center, zoom };
};

/**
 * Calculate radius from a viewport bounding box.
 * Uses the Haversine formula to find the distance from the center to a corner
 * of the viewport, which gives a radius that covers the entire area.
 */
const radiusFromViewport = (viewport: google.maps.LatLngBounds): number => {
  const ne = viewport.getNorthEast();
  const sw = viewport.getSouthWest();
  const centerLat = (ne.lat() + sw.lat()) / 2;
  const centerLng = (ne.lng() + sw.lng()) / 2;

  // Haversine distance from center to NE corner
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371000; // Earth radius in meters
  const dLat = toRad(ne.lat() - centerLat);
  const dLng = toRad(ne.lng() - centerLng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(centerLat)) * Math.cos(toRad(ne.lat())) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const radius = R * c;

  // Clamp between 50m and 50km for reasonable geofences
  return Math.max(50, Math.min(radius, 50000));
};

export default function GeofenceMapEditor({ 
  initialGeometry,
  geometryType = 'polygon',
  onGeometryChange,
  onCancel 
}: GeofenceMapEditorProps) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
  });

  // Pre-compute map center from existing geofence so it opens in the right spot
  const initialView = computeCenterFromGeometry(initialGeometry);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(geometryType);
  const [currentShape, setCurrentShape] = useState<google.maps.Circle | google.maps.Polygon | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const currentShapeRef = useRef<Shape | null>(null);
  const draftShapeRef = useRef<Shape | null>(null);
  const draftPathRef = useRef<LatLngLiteral[]>([]);
  const circleStartRef = useRef<LatLngLiteral | null>(null);
  const shapeListenersRef = useRef<google.maps.MapsEventListener[]>([]);

  useEffect(() => {
    if (map && initialGeometry) {
      // Load initial geometry if editing
      loadInitialGeometry();
    }
  }, [map, initialGeometry]);

  const clearShapeListeners = useCallback(() => {
    shapeListenersRef.current.forEach((listener) => listener.remove());
    shapeListenersRef.current = [];
  }, []);

  const clearDraftShape = useCallback(() => {
    draftShapeRef.current?.setMap(null);
    draftShapeRef.current = null;
    draftPathRef.current = [];
    circleStartRef.current = null;
    map?.setOptions({ draggable: true });
  }, [map]);

  useEffect(() => {
    const cancelPendingCircle = (event: Event) => {
      if (!circleStartRef.current) return;
      if (
        event.type === 'mouseup' &&
        typeof Node !== 'undefined' &&
        event.target instanceof Node &&
        mapContainerRef.current?.contains(event.target)
      ) {
        return;
      }
      clearDraftShape();
    };

    window.addEventListener('mouseup', cancelPendingCircle);
    window.addEventListener('pointercancel', cancelPendingCircle);
    window.addEventListener('blur', cancelPendingCircle);

    return () => {
      window.removeEventListener('mouseup', cancelPendingCircle);
      window.removeEventListener('pointercancel', cancelPendingCircle);
      window.removeEventListener('blur', cancelPendingCircle);
    };
  }, [clearDraftShape]);

  const removeCurrentShape = useCallback(() => {
    currentShapeRef.current?.setMap(null);
    currentShapeRef.current = null;
    clearShapeListeners();
    setCurrentShape(null);
  }, [clearShapeListeners]);

  const updateShapeGeometry = useCallback(
    (shape: google.maps.Circle | google.maps.Polygon) => {
      if (shape instanceof google.maps.Circle) {
        const center = shape.getCenter()!;
        const radius = shape.getRadius();
        const centerLiteral = { lat: center.lat(), lng: center.lng() };
        const wkt = circleToPolygonWkt(centerLiteral, radius);

        onGeometryChange({
          type: 'Circle',
          center: centerLiteral,
          radius: radius,
          wkt: wkt,
        });
        return;
      }

      const path = shape.getPath();
      const coordinates: string[] = [];
      const geoJsonCoordinates: number[][] = [];

      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        coordinates.push(`${point.lng()} ${point.lat()}`);
        geoJsonCoordinates.push([point.lng(), point.lat()]);
      }

      if (path.getLength() > 0) {
        const firstPoint = path.getAt(0);
        coordinates.push(`${firstPoint.lng()} ${firstPoint.lat()}`);
        geoJsonCoordinates.push([firstPoint.lng(), firstPoint.lat()]);
      }

      const wkt = `POLYGON ((${coordinates.join(', ')}))`;

      onGeometryChange({
        type: 'Polygon',
        coordinates: [geoJsonCoordinates],
        wkt: wkt,
      });
    },
    [onGeometryChange]
  );

  const attachShapeListeners = useCallback(
    (shape: google.maps.Circle | google.maps.Polygon) => {
      clearShapeListeners();

      if (shape instanceof google.maps.Circle) {
        shapeListenersRef.current = [
          shape.addListener('center_changed', () => updateShapeGeometry(shape)),
          shape.addListener('radius_changed', () => updateShapeGeometry(shape)),
          shape.addListener('dragend', () => updateShapeGeometry(shape)),
        ];
        return;
      }

      const path = shape.getPath();
      shapeListenersRef.current = [
        path.addListener('insert_at', () => updateShapeGeometry(shape)),
        path.addListener('remove_at', () => updateShapeGeometry(shape)),
        path.addListener('set_at', () => updateShapeGeometry(shape)),
        shape.addListener('dragend', () => updateShapeGeometry(shape)),
      ];
    },
    [clearShapeListeners, updateShapeGeometry]
  );

  const updateDraftPolygon = useCallback(
    (path: LatLngLiteral[]) => {
      if (!map) return;

      if (!(draftShapeRef.current instanceof google.maps.Polygon)) {
        draftShapeRef.current?.setMap(null);
        draftShapeRef.current = new google.maps.Polygon({
          paths: path,
          ...GEOFENCE_DRAWING_OPTIONS.polygon,
          editable: false,
          draggable: false,
          map,
        });
        return;
      }

      draftShapeRef.current.setPath(path);
    },
    [map]
  );

  const updateDraftCircle = useCallback(
    (center: LatLngLiteral, edge: LatLngLiteral) => {
      if (!map) return;
      const radius = distanceInMeters(center, edge);

      if (!(draftShapeRef.current instanceof google.maps.Circle)) {
        draftShapeRef.current?.setMap(null);
        draftShapeRef.current = new google.maps.Circle({
          center,
          radius,
          ...GEOFENCE_DRAWING_OPTIONS.circle,
          editable: false,
          draggable: false,
          map,
        });
        return;
      }

      draftShapeRef.current.setCenter(center);
      draftShapeRef.current.setRadius(radius);
    },
    [map]
  );

  const applyNewShape = useCallback(
    (shape: Shape) => {
      removeCurrentShape();
      currentShapeRef.current = shape;
      setCurrentShape(shape);
      attachShapeListeners(shape);
      updateShapeGeometry(shape);
    },
    [attachShapeListeners, removeCurrentShape, updateShapeGeometry]
  );

  const loadInitialGeometry = useCallback(() => {
    if (!map || !initialGeometry) return;

    try {
      // Normalize: handles GeoJSON objects, WKT strings, EWKT strings
      const normalized = normalizeGeometry(initialGeometry);
      if (!normalized?.coordinates?.[0]?.length) {
        console.warn('Could not parse initial geometry:', initialGeometry);
        return;
      }

      const coordinates = normalized.coordinates[0].map((coord: number[]) => ({
        lat: coord[1],
        lng: coord[0],
      }));

      const polygon = new google.maps.Polygon({
        paths: coordinates,
        ...GEOFENCE_DRAWING_OPTIONS.polygon,
        map: map,
      });
      applyNewShape(polygon);

      const bounds = new google.maps.LatLngBounds();
      coordinates.forEach((coord: any) => bounds.extend(coord));
      map.fitBounds(bounds, 40); // 40px padding for a better view
    } catch (error) {
      console.error('Error loading initial geometry:', error);
    }
  }, [map, initialGeometry, applyNewShape]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== 'polygon') return;
      const point = toLatLngLiteral(event.latLng);
      if (!point) return;

      const nextPath = [...draftPathRef.current, point];
      draftPathRef.current = nextPath;
      updateDraftPolygon(nextPath);
    },
    [drawingMode, updateDraftPolygon]
  );

  const handleMapDoubleClick = useCallback(() => {
    if (drawingMode !== 'polygon') return;
    const path = removeConsecutiveDuplicatePoints(draftPathRef.current);
    if (!map || path.length < 3) return;

    const polygon = new google.maps.Polygon({
      paths: path,
      ...GEOFENCE_DRAWING_OPTIONS.polygon,
      map,
    });
    applyNewShape(polygon);
    clearDraftShape();
  }, [applyNewShape, clearDraftShape, drawingMode, map]);

  const handleMapMouseDown = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== 'circle') return;
      const center = toLatLngLiteral(event.latLng);
      if (!center || !map) return;

      circleStartRef.current = center;
      updateDraftCircle(center, center);
      map.setOptions({ draggable: false });
    },
    [drawingMode, map, updateDraftCircle]
  );

  const handleMapMouseMove = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== 'circle' || !circleStartRef.current) return;
      const edge = toLatLngLiteral(event.latLng);
      if (edge) updateDraftCircle(circleStartRef.current, edge);
    },
    [drawingMode, updateDraftCircle]
  );

  const handleMapMouseUp = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== 'circle' || !circleStartRef.current || !map) return;

      const center = circleStartRef.current;
      const edge = toLatLngLiteral(event.latLng) ?? center;
      const radius = distanceInMeters(center, edge);
      map.setOptions({ draggable: true });

      if (radius < 1) {
        clearDraftShape();
        return;
      }

      const circle = new google.maps.Circle({
        center,
        radius,
        ...GEOFENCE_DRAWING_OPTIONS.circle,
        map,
      });
      applyNewShape(circle);
      clearDraftShape();
    },
    [applyNewShape, clearDraftShape, drawingMode, map]
  );

  const handleLocationSearch = useCallback(() => {
    if (!map || !searchQuery.trim()) return;
    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
    geocoderRef.current.geocode(
      { address: searchQuery.trim(), region: 'pt' },
      (results, status) => {
        if (status !== 'OK' || !results?.[0]?.geometry?.location) {
          Alert.alert('Localização inválida', 'Não foi possível encontrar a localização.');
          return;
        }
        const place = results[0];
        const location = place.geometry.location;
        const center = { lat: location.lat(), lng: location.lng() };

        // Use viewport to calculate a radius that matches the result's area
        const viewport = place.geometry.viewport;
        const radius = viewport
          ? radiusFromViewport(viewport)
          : DEFAULT_ADDRESS_RADIUS_METERS;

        const circle = new google.maps.Circle({
          center,
          radius,
          ...GEOFENCE_DRAWING_OPTIONS.circle,
          map: map,
        });
        applyNewShape(circle);

        // Fit the map to the circle bounds
        map.fitBounds(circle.getBounds()!);
      }
    );
  }, [map, searchQuery, applyNewShape]);

  const handleClearShape = useCallback(() => {
    if (!currentShapeRef.current) return;
    removeCurrentShape();
    clearDraftShape();
    onGeometryChange({ type: 'Polygon', wkt: '' });
  }, [clearDraftShape, onGeometryChange, removeCurrentShape]);

  useEffect(() => {
    setDrawingMode(geometryType);
    clearDraftShape();
  }, [clearDraftShape, geometryType]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    removeCurrentShape();
    clearDraftShape();
    setMap(null);
  }, [clearDraftShape, removeCurrentShape]);

  if (loadError) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle-outline" size={48} color="#FF3B30" />
        <Text style={styles.errorText}>Erro ao carregar Google Maps</Text>
        <Text style={styles.errorSubtext}>
          Verifique a chave da API e ative Maps JavaScript API + Geocoding API
        </Text>
      </View>
    );
  }

  if (!isLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>Carregando mapa...</Text>
      </View>
    );
  }

  const searchContainerStyle = StyleSheet.flatten(styles.searchContainer) as Record<string, unknown>;
  const containerStyle = StyleSheet.flatten(styles.container) as Record<string, unknown>;

  return (
    <div style={containerStyle}>
      {/* Search Bar */}
      <div style={searchContainerStyle}>
        <Ionicons name="search" size={20} color="#8E8E93" style={styles.searchIcon} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleLocationSearch();
          }}
          placeholder="Procurar localização (ex: Lisboa, Portugal)"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: 15,
            padding: '12px 0',
            backgroundColor: 'transparent',
          }}
        />
        <TouchableOpacity style={styles.searchButton} onPress={handleLocationSearch}>
          <Text style={styles.searchButtonText}>Procurar</Text>
        </TouchableOpacity>
      </div>

      {/* Actions */}
      <View style={styles.actionsRow}>
        <TouchableOpacity
          style={styles.clearButton}
          onPress={handleClearShape}
          disabled={!currentShape}
        >
          <Ionicons name="trash-outline" size={20} color={currentShape ? '#FF3B30' : '#C7C7CC'} />
          <Text style={[
            styles.clearButtonText,
            !currentShape && styles.clearButtonTextDisabled
          ]}>
            Limpar
          </Text>
        </TouchableOpacity>
      </View>

      {/* Google Map */}
      <div
        ref={mapContainerRef}
        style={{ width: '100%', height: 320, marginBottom: 12, borderRadius: 8, overflow: 'hidden' }}
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={initialView?.center ?? GOOGLE_MAPS_CONFIG.defaultCenter}
          zoom={initialView?.zoom ?? GOOGLE_MAPS_CONFIG.defaultZoom}
          options={{
            ...GOOGLE_MAPS_CONFIG.mapOptions,
            disableDoubleClickZoom: drawingMode === 'polygon',
          }}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          onDblClick={handleMapDoubleClick}
          onMouseDown={handleMapMouseDown}
          onMouseMove={handleMapMouseMove}
          onMouseUp={handleMapMouseUp}
        />
      </div>

      {/* Instructions */}
      <View style={styles.instructions}>
        <Text style={styles.instructionsTitle}>Instruções</Text>
        <Text style={styles.instructionsText}>
          • Use a pesquisa para criar uma área automaticamente
        </Text>
        <Text style={styles.instructionsText}>
          • No mapa: clique para adicionar pontos, duplo clique para finalizar o polígono
        </Text>
        <Text style={styles.instructionsText}>
          • Para círculo: clique e arraste para definir o raio
        </Text>
        <Text style={styles.instructionsText}>
          • Use Limpar para remover e recomeçar
        </Text>
      </View>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    height: 400,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F9F9F9',
    borderRadius: 8,
  },
  loadingText: {
    fontSize: 16,
    color: '#8E8E93',
  },
  errorContainer: {
    height: 400,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFF5F5',
    borderRadius: 8,
    padding: 20,
  },
  errorText: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: '600',
    color: '#FF3B30',
  },
  errorSubtext: {
    marginTop: 4,
    fontSize: 14,
    color: '#8E8E93',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9F9F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#007AFF',
    borderRadius: 8,
    justifyContent: 'center',
  },
  searchButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 12,
  },
  clearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    backgroundColor: '#FFFFFF',
  },
  clearButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FF3B30',
  },
  clearButtonTextDisabled: {
    color: '#C7C7CC',
  },
  instructions: {
    backgroundColor: '#E5F1FF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  instructionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
    marginBottom: 8,
  },
  instructionsText: {
    fontSize: 13,
    color: '#007AFF',
    marginBottom: 4,
  },
});
